/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Bring email engagement data into your warehouse", description = "Export email engagement events from Salesforce Marketing Cloud, Braze, Iterable, Klaviyo, or Marketo to your warehouse for use in Customer Studio.", lastUpdatedDate = new Date(1792454400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Bring email engagement data into your warehouse",
        description: "Export email engagement events from Salesforce Marketing Cloud, Braze, Iterable, Klaviyo, or Marketo to your warehouse for use in Customer Studio.",
        lastUpdatedDate: "Tue Oct 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Why export email engagement data to your warehouse",
          "depth": 0,
          "id": "why-export-email-engagement-data-to-your-warehouse"
        }, {
          "value": "Plan your export",
          "depth": 0,
          "id": "plan-your-export"
        }, {
          "value": "Standardize engagement events",
          "depth": 0,
          "id": "standardize-engagement-events"
        }, {
          "value": "Choose an export method",
          "depth": 0,
          "id": "choose-an-export-method"
        }, {
          "value": "Salesforce Marketing Cloud",
          "depth": 1,
          "id": "salesforce-marketing-cloud"
        }, {
          "value": "Braze",
          "depth": 1,
          "id": "braze"
        }, {
          "value": "Iterable",
          "depth": 1,
          "id": "iterable"
        }, {
          "value": "Klaviyo",
          "depth": 1,
          "id": "klaviyo"
        }, {
          "value": "Marketo",
          "depth": 1,
          "id": "marketo"
        }, {
          "value": "Add engagement events to Customer Studio",
          "depth": 0,
          "id": "add-engagement-events-to-customer-studio"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data engineers, marketing operations"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["A Customer Studio ", _jsx(_components.a, {
                href: "/customer-studio/schema#create-the-parent-model",
                children: "parent model"
              }), " for your people"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Export email sends, opens, and clicks from your email service provider (ESP) to your warehouse. Use this data in Customer Studio to find engaged customers, exclude inactive subscribers, and follow up based on email activity."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "why-export-email-engagement-data-to-your-warehouse",
        children: "Why export email engagement data to your warehouse"
      }), "\n", _jsx(_components.p, {
        children: "Export email engagement data to your warehouse to use it alongside purchases, product usage, and other customer data."
      }), "\n", _jsx(_components.p, {
        children: "With this data in your Customer Studio schema, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Build engagement-based audiences."
          }), " Find customers who clicked an email link in the last 30 days."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Exclude inactive subscribers."
          }), " Identify customers who haven't clicked an email recently."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Follow up based on engagement."
          }), " Use a ", _jsx(_components.a, {
            href: "/customer-studio/journeys/tiles#hold-until",
            children: "Hold until"
          }), " tile in a journey to send a follow-up when someone doesn't click within three days."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Use engagement data in AI Decisioning."
          }), " Make email engagement events available to ", _jsx(_components.a, {
            href: "/ai-decisioning/prepare-data#3-create-event-models",
            children: "AI Decisioning"
          }), " as campaign events."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "plan-your-export",
        children: "Plan your export"
      }), "\n", _jsx(_components.p, {
        children: "Before choosing an export method, consider these requirements and best practices:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Check your ESP plan."
          }), " Some export methods are paid add-ons, in beta, or turned on by your ESP account team. The ", _jsx(_components.strong, {
            children: "Access"
          }), " column for each ESP below shows which. Confirm access before you set up your pipeline."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Plan for historical and new events."
          }), " Some export methods only collect new events. If you need historical data, load older events before setting up ongoing exports."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Monitor for missing data."
          }), " Monitor for missing files or events, not just failed jobs. If an export stops working and your ESP no longer retains the missing events, you may not be able to recover them."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Use clicks as a stronger engagement signal than opens."
          }), " Email privacy features, such as Apple Mail Privacy Protection, can record opens even when someone hasn't read a message. A click requires someone to select a link."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Match events to your customers."
          }), " ESPs use their own identifiers, which may not match your warehouse records. When multiple profiles share an email address, matching by email alone can associate events with the wrong customer. Use your own customer IDs or ", _jsx(_components.a, {
            href: "https://hightouch.com/platform/identity-resolution",
            children: "Hightouch Identity Resolution"
          }), " to connect events to the correct customers."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "standardize-engagement-events",
        children: "Standardize engagement events"
      }), "\n", _jsxs(_components.p, {
        children: ["Map events from each ESP to a consistent set of columns in your warehouse or data pipeline before creating event models in Customer Studio. This lets you use the same audience filters and engagement calculations across providers. Some columns, such as ", _jsx(_components.code, {
          children: "event_type"
        }), " and ", _jsx(_components.code, {
          children: "customer_id"
        }), ", may need to be derived or populated during this process."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event_id"
              })
            }), _jsxs(_components.td, {
              children: ["A unique identifier for each event. Use the ESP event ID when available, such as Marketo's ", _jsx(_components.code, {
                children: "marketoGUID"
              }), " or Klaviyo's ", _jsx(_components.code, {
                children: "uuid"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event_type"
              })
            }), _jsxs(_components.td, {
              children: ["The event type, such as ", _jsx(_components.code, {
                children: "sent"
              }), ", ", _jsx(_components.code, {
                children: "delivered"
              }), ", ", _jsx(_components.code, {
                children: "opened"
              }), ", ", _jsx(_components.code, {
                children: "clicked"
              }), ", or ", _jsx(_components.code, {
                children: "bounced"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event_timestamp"
              })
            }), _jsxs(_components.td, {
              children: ["When the event occurred, converted to UTC. Store the time the event was loaded separately, such as in ", _jsx(_components.code, {
                children: "ingested_at"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "source_user_id"
              })
            }), _jsx(_components.td, {
              children: "The recipient's identifier in the ESP."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "customer_id"
              })
            }), _jsx(_components.td, {
              children: "The customer identifier used in your warehouse. Don't expect the ESP to provide it."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "email"
              })
            }), _jsx(_components.td, {
              children: "The recipient's email address. Don't rely on email alone to identify customers."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign_id"
              })
            }), _jsx(_components.td, {
              children: "The campaign identifier provided by the ESP."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_id"
              })
            }), _jsx(_components.td, {
              children: "The message identifier provided by the ESP. Depending on the ESP, it can identify a message definition, a send, or a dispatch."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_name"
              })
            }), _jsx(_components.td, {
              children: "The campaign, journey, or message name."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "url"
              })
            }), _jsx(_components.td, {
              children: "The clicked URL, when available. Use it to build audiences based on clicked links."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "is_unique"
              })
            }), _jsx(_components.td, {
              children: "Whether the ESP identifies the event as unique, when available. Some ESPs deduplicate events before you export them."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "raw_event"
              })
            }), _jsx(_components.td, {
              children: "Optional. The original event data, which you can use to troubleshoot or remap fields."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "If your ESP doesn't provide an event ID, generate one from fields that uniquely identify the event. Make sure the ID stays the same across repeated exports so you can identify duplicates. If you combine data from multiple ESPs, include the provider in the identifier."
      }), "\n", _jsx(_components.h2, {
        id: "choose-an-export-method",
        children: "Choose an export method"
      }), "\n", _jsx(_components.p, {
        children: "Choose your ESP below to compare export methods, historical data availability, and field mappings."
      }), "\n", _jsx(_components.p, {
        children: "Availability and limits vary by ESP and plan. Check the linked ESP documentation before you set up your export."
      }), "\n", _jsx(_components.h3, {
        id: "salesforce-marketing-cloud",
        children: "Salesforce Marketing Cloud"
      }), "\n", _jsxs(_components.p, {
        children: ["Both export methods use scheduled automations in ", _jsx(_components.a, {
          href: "https://help.salesforce.com/s/articleView?id=mktg.mc_as_automation_studio.htm&type=5",
          children: "Automation Studio"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Add a ", _jsx(_components.strong, {
          children: "File Transfer"
        }), " activity to move exported files to your SFTP server or cloud storage. Without this step, the files stay in Marketing Cloud, even when the automation completes successfully."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Method"
            }), _jsx(_components.th, {
              children: "Access"
            }), _jsx(_components.th, {
              children: "History"
            }), _jsx(_components.th, {
              children: "Output"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "https://help.salesforce.com/s/articleView?id=mktg.mc_as_data_views.htm&type=5",
                children: "Data views"
              }), " with SQL"]
            }), _jsx(_components.td, {
              children: "Requires Automation Studio"
            }), _jsx(_components.td, {
              children: "The last six months"
            }), _jsx(_components.td, {
              children: "One CSV, shaped by your query"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://help.salesforce.com/s/articleView?id=mktg.mc_as_tracking_extract.htm&type=5",
                children: "Tracking extract"
              })
            }), _jsx(_components.td, {
              children: "Requires Automation Studio"
            }), _jsx(_components.td, {
              children: "Up to six months"
            }), _jsx(_components.td, {
              children: "A .zip with one CSV per event type you select"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Each method is a sequence of Automation Studio activities. Put each activity in its own step so that each one starts only after the previous one finishes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data views:"
          }), " A ", _jsx(_components.strong, {
            children: "SQL Query"
          }), " activity writes the events to a data extension, a ", _jsx(_components.strong, {
            children: "Data Extract"
          }), " activity exports the data extension to a file, and a ", _jsx(_components.strong, {
            children: "File Transfer"
          }), " activity moves the file."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Tracking extract:"
          }), " A ", _jsx(_components.strong, {
            children: "Data Extract"
          }), " activity with the tracking extract type creates a .zip file, and a ", _jsx(_components.strong, {
            children: "File Transfer"
          }), " activity moves it. End the output file name in ", _jsx(_components.code, {
            children: ".zip"
          }), ". Select at least the ", _jsx(_components.strong, {
            children: "Sent"
          }), ", ", _jsx(_components.strong, {
            children: "Opens"
          }), ", ", _jsx(_components.strong, {
            children: "Clicks"
          }), ", and ", _jsx(_components.strong, {
            children: "SendJobs"
          }), " outputs. The other outputs identify each send only by ID, and ", _jsx(_components.strong, {
            children: "SendJobs"
          }), " adds details like the email name."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Use data views if someone on your team writes SQL and you want one file with the columns you choose. Use tracking extract to export without SQL, and plan to unzip the file and load each CSV into its own table."
      }), "\n", _jsxs(_components.p, {
        children: ["Filter each run to a rolling date range that overlaps the previous run, such as the last seven days, instead of fixed dates, so no events fall between runs. Remove the duplicates from overlapping runs with ", _jsx(_components.code, {
          children: "event_id"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Map Marketing Cloud fields to the ", _jsx(_components.a, {
          href: "#standardize-engagement-events",
          children: "standard columns"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Marketing Cloud field"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "source_user_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "SubscriberKey"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event_timestamp"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "EventDate"
              }), ", converted to UTC"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "JobID"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_name"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "EmailName"
              }), ", from ", _jsx(_components.code, {
                children: "_Job"
              }), " or ", _jsx(_components.code, {
                children: "SendJobs"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "url"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "URL"
              }), ", on clicks only"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "is_unique"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "IsUnique"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Before you combine Marketing Cloud events with other sources, convert ", _jsx(_components.code, {
          children: "EventDate"
        }), " to UTC. Marketing Cloud stores it in Central Standard Time and doesn't adjust it for daylight saving time."]
      }), "\n", _jsx(_components.h3, {
        id: "braze",
        children: "Braze"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Method"
            }), _jsx(_components.th, {
              children: "Access"
            }), _jsx(_components.th, {
              children: "History"
            }), _jsx(_components.th, {
              children: "Output"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.braze.com/docs/user_guide/data/distribution/braze_currents/setting_up_currents",
                children: "Currents"
              })
            }), _jsx(_components.td, {
              children: "Included with some Braze packages"
            }), _jsx(_components.td, {
              children: "From the day you turn it on"
            }), _jsx(_components.td, {
              children: "Files in Amazon S3, Google Cloud Storage, or Azure Blob Storage"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.braze.com/docs/partners/data_and_analytics/data_warehouses/snowflake",
                children: "Snowflake Data Sharing"
              })
            }), _jsx(_components.td, {
              children: "Contact your Braze account manager"
            }), _jsx(_components.td, {
              children: "The most recent two years"
            }), _jsx(_components.td, {
              children: "Shared tables in your Snowflake account or a Braze-provided reader account"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "If your warehouse is Snowflake, use Snowflake Data Sharing. There's no pipeline to maintain, and it includes two years of history. Otherwise, turn on Currents as early as you can, since its history starts the day you connect it."
      }), "\n", _jsx(_components.p, {
        children: "If your Currents connector's credentials expire, Braze stops sending events, and after five days it drops them permanently. Renew credentials before they expire, and monitor your storage bucket for missing files."
      }), "\n", _jsxs(_components.p, {
        children: ["If you don't have a Snowflake account, Braze can create a ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/data/distribution/braze_currents/use_cases/how_braze_uses_currents#snowflake-reader-accounts",
          children: "reader account"
        }), " for you and share your data into it. If you use a reader account, copy the shared data into your own warehouse before you join it with other customer data. A reader account can only query the shared tables."]
      }), "\n", _jsxs(_components.p, {
        children: ["Map Braze fields to the ", _jsx(_components.a, {
          href: "#standardize-engagement-events",
          children: "standard columns"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Braze field"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "source_user_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "external_user_id"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event_timestamp"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "time"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign_id"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "campaign_id"
              }), " in Currents, ", _jsx(_components.code, {
                children: "CAMPAIGN_API_ID"
              }), " in Snowflake Data Sharing"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_id"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "dispatch_id"
              }), ", for scheduled campaigns"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_name"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "campaign_name"
              }), " or ", _jsx(_components.code, {
                children: "canvas_name"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "url"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "url"
              }), ", on clicks only"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "is_unique"
              })
            }), _jsx(_components.td, {
              children: "Not provided"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["A Braze ", _jsx(_components.code, {
          children: "campaign_id"
        }), " covers every send of a recurring campaign, such as a weekly newsletter. To report on each send separately, use ", _jsx(_components.code, {
          children: "dispatch_id"
        }), " for scheduled campaigns. Action-based campaigns, API-triggered campaigns, and Canvas steps get a different ", _jsx(_components.code, {
          children: "dispatch_id"
        }), " for each recipient, so you can't use it to group the recipients of one send."]
      }), "\n", _jsx(_components.h3, {
        id: "iterable",
        children: "Iterable"
      }), "\n", _jsx(_components.p, {
        children: "To include historical engagement, use the Export API to load older events before enabling Data Sync or Snowflake Secure Data Sharing for ongoing updates."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Method"
            }), _jsx(_components.th, {
              children: "Access"
            }), _jsx(_components.th, {
              children: "History"
            }), _jsx(_components.th, {
              children: "Output"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://support.iterable.com/hc/en-us/articles/204780579-Iterable-API-Endpoints-and-Sample-Payloads",
                children: "Export API"
              })
            }), _jsx(_components.td, {
              children: "Included"
            }), _jsx(_components.td, {
              children: "Includes history"
            }), _jsx(_components.td, {
              children: "Files or API responses you load yourself"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://support.iterable.com/hc/en-us/articles/45730014531988-Data-Sync-Overview",
                children: "Data Sync"
              })
            }), _jsx(_components.td, {
              children: "Contact your Iterable account team"
            }), _jsx(_components.td, {
              children: "From the day you turn it on"
            }), _jsx(_components.td, {
              children: "Databricks, BigQuery, Redshift, Amazon S3, or SFTP"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://support.iterable.com/hc/en-us/articles/4404943746708-Snowflake-Secure-Data-Sharing-Iterable-Integration",
                children: "Snowflake Secure Data Sharing"
              })
            }), _jsx(_components.td, {
              children: "Contact your Iterable account team"
            }), _jsx(_components.td, {
              children: "From the day you turn it on"
            }), _jsx(_components.td, {
              children: "Shared tables in your Snowflake account"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For near real-time events, Iterable ", _jsx(_components.a, {
          href: "https://support.iterable.com/hc/en-us/articles/208013936-System-Webhooks",
          children: "system webhooks"
        }), " send sends, opens, clicks, bounces, and other events as they happen. If you use Hightouch Events, send them to a ", _jsx(_components.a, {
          href: "/events/sources/webhook",
          children: "webhook event source"
        }), ", which can load them into your warehouse. Webhooks only send new events, so load older events with the Export API."]
      }), "\n", _jsx(_components.p, {
        children: "Before you set up an Iterable export, check these details:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Use your project's API host."
          }), " US projects use ", _jsx(_components.code, {
            children: "api.iterable.com"
          }), " and EU projects use ", _jsx(_components.code, {
            children: "api.eu.iterable.com"
          }), ". Data Sync also requires you to allow an IP address for your project's region."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Check data type names."
          }), " The unsubscribe data type is ", _jsx(_components.code, {
            children: "emailUnSubscribe"
          }), ", with a capital S."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Deduplicate after a full refresh."
          }), " For Amazon S3, a Data Sync full refresh adds the full dataset again without deleting existing files. Remove duplicates with ", _jsx(_components.code, {
            children: "event_id"
          }), " to avoid double-counting events."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Map Iterable fields to the ", _jsx(_components.a, {
          href: "#standardize-engagement-events",
          children: "standard columns"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Iterable field"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "source_user_id"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "userId"
              }), " or ", _jsx(_components.code, {
                children: "email"
              }), ", depending on your project. Snowflake Secure Data Sharing event tables include ", _jsx(_components.code, {
                children: "EMAIL"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event_timestamp"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "createdAt"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaignId"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "messageId"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_name"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "CAMPAIGN_NAME"
              }), " in Snowflake Secure Data Sharing, or campaign data from Data Sync"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "url"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "url"
              }), ", on clicks only"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "is_unique"
              })
            }), _jsx(_components.td, {
              children: "Not documented"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "klaviyo",
        children: "Klaviyo"
      }), "\n", _jsx(_components.p, {
        children: "Data warehouse sync is part of Advanced KDP, which isn't included in Klaviyo's standard marketing plan. Without it, use the Events API for ongoing exports. The Bulk Export API is invite-only during beta, so request access from Klaviyo before you plan a backfill around it."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Method"
            }), _jsx(_components.th, {
              children: "Access"
            }), _jsx(_components.th, {
              children: "History"
            }), _jsx(_components.th, {
              children: "Output"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.klaviyo.com/en/reference/get_events",
                children: "Events API"
              })
            }), _jsx(_components.td, {
              children: "Included"
            }), _jsx(_components.td, {
              children: "Includes history, slow at volume"
            }), _jsx(_components.td, {
              children: "API responses you load yourself"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.klaviyo.com/en/reference/bulk_export_api_overview",
                children: "Bulk Export API"
              })
            }), _jsx(_components.td, {
              children: "Invite-only beta"
            }), _jsx(_components.td, {
              children: "Includes history"
            }), _jsx(_components.td, {
              children: "A downloaded file per export job"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://help.klaviyo.com/hc/en-us/articles/17759932376475",
                children: "Data warehouse sync"
              })
            }), _jsx(_components.td, {
              children: "Requires an Advanced KDP subscription"
            }), _jsx(_components.td, {
              children: "Up to all history, chosen when you connect"
            }), _jsx(_components.td, {
              children: "Hourly syncs to Snowflake, BigQuery, Redshift, Databricks, Azure Synapse, or Amazon S3"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["When you map ", _jsx(_components.code, {
          children: "event_type"
        }), ", use Klaviyo's metric names: ", _jsx(_components.strong, {
          children: "Received Email"
        }), " maps to ", _jsx(_components.code, {
          children: "delivered"
        }), ", ", _jsx(_components.strong, {
          children: "Opened Email"
        }), " to ", _jsx(_components.code, {
          children: "opened"
        }), ", and ", _jsx(_components.strong, {
          children: "Clicked Email"
        }), " to ", _jsx(_components.code, {
          children: "clicked"
        }), ". Klaviyo records ", _jsx(_components.strong, {
          children: "Received Email"
        }), " when a recipient receives an email and doesn't record a separate send event, so Klaviyo counts won't match send counts from other ESPs. ", _jsx(_components.code, {
          children: "Campaign Name"
        }), " can repeat across campaigns, so use ", _jsx(_components.code, {
          children: "$message"
        }), " to identify a specific message."]
      }), "\n", _jsxs(_components.p, {
        children: ["Map Klaviyo fields to the ", _jsx(_components.a, {
          href: "#standardize-engagement-events",
          children: "standard columns"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Klaviyo field"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "source_user_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "profile_id"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event_timestamp"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "datetime"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign_id"
              })
            }), _jsxs(_components.td, {
              children: ["No direct equivalent. Use ", _jsx(_components.code, {
                children: "$message"
              }), " for campaigns and ", _jsx(_components.code, {
                children: "$flow"
              }), " for flows."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "$message"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_name"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Campaign Name"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "url"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "URL"
              }), ", on clicks only"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "is_unique"
              })
            }), _jsx(_components.td, {
              children: "Not documented"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "marketo",
        children: "Marketo"
      }), "\n", _jsx(_components.p, {
        children: "Both Marketo Engage export methods are APIs, so you build and maintain the integration."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Method"
            }), _jsx(_components.th, {
              children: "Access"
            }), _jsx(_components.th, {
              children: "History"
            }), _jsx(_components.th, {
              children: "Output"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://experienceleague.adobe.com/en/docs/marketo-developer/marketo/rest/bulk-extract/bulk-activity-extract",
                children: "Bulk Activity Extract API"
              })
            }), _jsx(_components.td, {
              children: "Requires an API user with activity access"
            }), _jsx(_components.td, {
              children: "Includes history, in chunks of up to 31 days"
            }), _jsx(_components.td, {
              children: "A downloaded CSV per export job"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://experienceleague.adobe.com/en/docs/marketo-developer/marketo/rest/lead-database/activities",
                children: "REST API"
              })
            }), _jsx(_components.td, {
              children: "Requires an API user with activity access"
            }), _jsx(_components.td, {
              children: "Includes history, slow at volume"
            }), _jsx(_components.td, {
              children: "API responses you load yourself"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Use the Bulk Activity Extract API for daily exports and backfills. It has a daily export quota of 500 MB, shared across leads, activities, program members, and custom objects. Exports that other teams run against the same Marketo instance count toward the same quota. Export files expire after seven days. If you reach the quota, use the REST API until the quota resets at midnight US Central Time."
      }), "\n", _jsxs(_components.p, {
        children: ["Map Marketo fields to the ", _jsx(_components.a, {
          href: "#standardize-engagement-events",
          children: "standard columns"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Marketo field"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "marketoGUID"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "source_user_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "leadId"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event_timestamp"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "activityDate"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign_id"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "campaignId"
              }), ", the smart campaign that triggered the email"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_id"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "primaryAttributeValueId"
              }), ", the email ID"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_name"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "primaryAttributeValue"
              }), ", the email name"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "url"
              })
            }), _jsxs(_components.td, {
              children: ["In the ", _jsx(_components.code, {
                children: "attributes"
              }), " JSON, on clicks only"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "is_unique"
              })
            }), _jsx(_components.td, {
              children: "Not applicable. Marketo logs one open per lead per email."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Marketo keeps send and delivery events for less time than open and click events, so a long backfill can include opens and clicks without their matching sends. If export jobs overlap, deduplicate on ", _jsx(_components.code, {
          children: "marketoGUID"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "add-engagement-events-to-customer-studio",
        children: "Add engagement events to Customer Studio"
      }), "\n", _jsx(_components.p, {
        children: "After loading engagement events into your warehouse, add them to your Customer Studio schema to use them in audiences and journeys."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Connect events to customers."
          }), " Join each event to your parent model using ", _jsx(_components.code, {
            children: "customer_id"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create event models."
          }), " ", _jsx(_components.a, {
            href: "/customer-studio/schema#create-an-event-model",
            children: "Create an event model"
          }), " for each event type you want to use, such as ", _jsx(_components.code, {
            children: "Email Sent"
          }), ", ", _jsx(_components.code, {
            children: "Email Opened"
          }), ", and ", _jsx(_components.code, {
            children: "Email Clicked"
          }), ". If you store all events in one table, filter each model by ", _jsx(_components.code, {
            children: "event_type"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Set the timestamp."
          }), " Select ", _jsx(_components.code, {
            children: "event_timestamp"
          }), " as the timestamp column."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Set the primary key."
          }), " Select ", _jsx(_components.code, {
            children: "event_id"
          }), " as the primary key. ", _jsx(_components.a, {
            href: "/customer-studio/journeys/create",
            children: "Event-based journeys"
          }), " require a primary key."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Verify your data."
          }), " ", _jsx(_components.a, {
            href: "/customer-studio/audience-filters#filter-on-events",
            children: "Filter on events"
          }), " to create a test audience of customers who clicked an email in the last 30 days. Compare the number of customers in the audience with the number of unique clickers your ESP reports for the same period."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Results may differ from your ESP reports because providers count events differently or exclude events associated with deleted profiles."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/data-model",
            children: "Plan your data model"
          }), " to decide how engagement events fit with the rest of your schema."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/audience-filters#filter-on-events",
            children: "Filter on events"
          }), " to build audiences and exclusion lists from engagement."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/prepare-data",
            children: "Prepare data for AI Decisioning"
          }), " to use engagement events as campaign events for AI Decisioning agents."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
